import type { Key } from "react-stately";

import { useMatchRoute, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";

import { usePageTitle } from "@/hooks/usePageTitle";

import { BillingShell } from "../components/BillingShell";
import { BILLING_TABS, billingTabLabel } from "../config/billing-tab-routes";
import { ADMIN_BILLING_TAB_CONFIG } from "./config/admin-billing-tab-routes";
import { useBillingAccountOverview } from "./overview/hooks/useBillingAccountOverview";

interface AdminBillingPageProps {
  readonly projectId: string;
}

export function AdminBillingPage({ projectId }: AdminBillingPageProps) {
  const { navigate } = useRouter();
  const matchRoute = useMatchRoute();
  const { data } = useBillingAccountOverview(Number(projectId));

  const getCurrentTab = (): string => {
    if (matchRoute({ to: "/billing/accounts/$projectId/invoices" }))
      return BILLING_TABS.INVOICES;
    if (matchRoute({ to: "/billing/accounts/$projectId/payment-methods" }))
      return BILLING_TABS.PAYMENT_METHODS;

    return BILLING_TABS.OVERVIEW;
  };

  const selectedTab = getCurrentTab();

  usePageTitle(`Billing - ${billingTabLabel(selectedTab as never)}`);

  const handleTabChange = useCallback(
    (key: Key) => {
      navigate({
        params: { projectId },
        to: `/billing/accounts/$projectId/${key.toString()}`,
      });
    },
    [projectId, navigate],
  );

  return (
    <BillingShell
      accountName={data?.account.name ?? ""}
      onTabChange={handleTabChange}
      selectedTab={selectedTab}
      tabs={ADMIN_BILLING_TAB_CONFIG}
    />
  );
}
